import { useRef, useEffect, useState, useCallback, useMemo, type ReactNode } from 'react'
import { toaster } from '@/components/ui/toaster'
import { locationUnavailableMessage } from '../../lib/locationHelp'
import { Box, HStack, IconButton, Text, Button } from '@chakra-ui/react'
import Map, { Source, Layer, Marker, MapRef } from 'react-map-gl/maplibre'
import type { LngLatBoundsLike, MapLayerMouseEvent } from 'maplibre-gl'
import maplibregl from 'maplibre-gl'
import * as GeoJSON from 'geojson'
import { FiBriefcase, FiCrosshair, FiX, FiZap } from 'react-icons/fi'
import { getMapStyle, MAP_LABEL_FONT } from '../../lib/mapConfig'
import { WALK_MAP_COLORS } from '../../theme/walkMapColors'
import { formatDistance, formatDuration } from '../../lib/walkGeo'
import { publishMapClick } from '../../lib/walkSimBridge'
import type { WalkStop } from '../../lib/walkStops'
import type { GeoPosition } from '../../hooks/useGeolocation'
import type { OptimizeBlockedReason } from '../../hooks/useWalkRoute'
import { ROUTE_STATE_LABEL, type RouteState } from '../../hooks/routeUiState'
import 'maplibre-gl/dist/maplibre-gl.css'

// ─── Layer / Source IDs (canvasser-specific, no conflict with TurfMap) ───

const SRC = {
  STOPS: 'canvasser-stops',
  ROUTE: 'canvasser-route',
  NEXT_SEGMENT: 'canvasser-next-segment',
} as const

const LYR = {
  STOP_CIRCLES: 'canvasser-stop-circles',
  STOP_LABELS: 'canvasser-stop-labels',
  ROUTE_LINE: 'canvasser-route-line',
  NEXT_SEGMENT_LINE: 'canvasser-next-segment-line',
} as const

// ─── GeoJSON builders ───────────────────────────────────────────

/**
 * [Spec §5.4] Pins read the same WalkState colour key as the list rows. Stops without
 * coordinates are skipped (they stay in the list, never on the map). Stops outside the
 * active filter/search are ghosted, not removed.
 */
export function stopsToGeoJson(
  stops: WalkStop[], visibleKeys: ReadonlySet<string> | null,
  sequence: ReadonlyMap<string, number>, selectedKey: string | null,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: stops.filter(s => s.lat != null && s.lng != null).map(s => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [s.lng!, s.lat!] },
      properties: {
        key: s.key,
        kind: s.kind,
        color: WALK_MAP_COLORS[s.state],
        seq: sequence.get(s.key) ?? null,
        isOpen: s.state !== 'done',
        isVisible: !visibleKeys || visibleKeys.has(s.key),
        isSelected: s.key === selectedKey,
        residentCount: s.residents.length,
      },
    })),
  }
}

function boundsOf(stops: WalkStop[]): LngLatBoundsLike | null {
  const pts = stops.filter(s => s.lat != null && s.lng != null)
  if (pts.length === 0) return null
  const lngs = pts.map(s => s.lng!), lats = pts.map(s => s.lat!)
  return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]]
}

/**
 * The ⚡ button's label + enabled state. Not enough stops → nothing to route. Routing unavailable →
 * retry (a plain re-route needs no GPS). Otherwise GPS and the trip cap gate an optimize; when one
 * could succeed ⚡ is live whether or not the current order is optimized.
 * A locked (organizer hand-made) order hides ⚡ — except as the routing-unavailable retry.
 */
export function reoptimizeButtonState(
  reason: OptimizeBlockedReason | null, routingUnavailable: boolean, _isRouteOptimized: boolean,
  orderLocked = false,
): { label: string; disabled: boolean; hidden: boolean } {
  if (reason === 'tooFew') return { label: 'Not enough stops to route', disabled: true, hidden: orderLocked }
  if (routingUnavailable) return { label: 'Routing unavailable — tap to retry', disabled: false, hidden: false }
  const hidden = orderLocked
  if (reason === 'noGps') return { label: 'GPS required to optimize', disabled: true, hidden }
  if (reason === 'tooMany') return { label: 'Too many stops to optimize', disabled: true, hidden }
  return { label: 'Re-plan route', disabled: false, hidden }
}

// ─── Component ──────────────────────────────────────────────────

export interface CanvasserMapViewProps {
  /** ALL stops (ghosting needs every one). */
  stops: WalkStop[]
  /** null = no filter/search active. */
  visibleKeys: ReadonlySet<string> | null
  sequence: ReadonlyMap<string, number>
  selectedKey: string | null
  onSelectStop: (key: string) => void
  activeFilterLabel: string | null
  routeGeoJson: GeoJSON.Feature<GeoJSON.LineString> | null
  nextSegmentGeoJson: GeoJSON.Feature<GeoJSON.LineString> | null
  gpsPosition: GeoPosition | null
  nextDistance: number | null
  nextDuration: number | null
  travelMode: 'walking' | 'driving'
  onTravelModeChange: (m: 'walking' | 'driving') => void
  onReoptimize: () => void
  isRouteOptimized: boolean
  /** useWalkRoute.optimizeBlockedReason — null when a ⚡ solve could succeed. */
  optimizeBlockedReason: OptimizeBlockedReason | null
  /** OSRM-1: backend returned the unrouted fallback — markers and list order only, no line. */
  routingUnavailable: boolean
  /** Resolves a fresh GPS fix (or null); the map flies to it. */
  onRecenter: () => Promise<GeoPosition | null>
  /** useWalkRoute.isFetching */
  isRefreshing: boolean
  /** [§15.1 S2] hybrid lists: mark worksite pins */
  showKindIcons: boolean
  /** [O1 P4-S8] Hand-made walk order: hides ⚡ (the routing-unavailable retry stays). */
  orderLocked?: boolean
  /** [A3 §5/§6] What the map says about the order in effect (chip). */
  routeState: RouteState
  /** [A3 §6] The one-time Re-plan tip is already dismissed (walk prefs). */
  replanTipSeen: boolean
  onDismissReplanTip: () => void
  /** In-flow status items shown under the chip/tip in the top overlay (offline badge/notice). */
  topStatus?: ReactNode
}

export function CanvasserMapView({
  stops,
  visibleKeys,
  sequence,
  selectedKey,
  onSelectStop,
  activeFilterLabel,
  routeGeoJson,
  nextSegmentGeoJson,
  gpsPosition,
  nextDistance,
  nextDuration,
  travelMode,
  onTravelModeChange,
  onReoptimize,
  isRouteOptimized,
  optimizeBlockedReason,
  routingUnavailable,
  onRecenter,
  isRefreshing,
  showKindIcons,
  orderLocked = false,
  routeState,
  replanTipSeen,
  onDismissReplanTip,
  topStatus,
}: CanvasserMapViewProps) {
  const mapRef = useRef<MapRef>(null)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const hasFitBoundsRef = useRef(false)

  const stopGeoJson = useMemo(
    () => stopsToGeoJson(stops, visibleKeys, sequence, selectedKey),
    [stops, visibleKeys, sequence, selectedKey],
  )

  // [§15.1 S2] DOM badges for visible worksite pins only — homes stay plain circles
  // (hundreds of DOM markers would be a perf risk).
  const worksiteBadgeStops = useMemo(
    () => showKindIcons
      ? stops.filter(s => s.kind === 'worksite' && s.lat != null && s.lng != null
          && (!visibleKeys || visibleKeys.has(s.key)))
      : [],
    [showKindIcons, stops, visibleKeys],
  )

  // Auto-fit bounds on initial load
  useEffect(() => {
    if (!isMapLoaded || !mapRef.current || hasFitBoundsRef.current) return
    const bounds = boundsOf(stops)
    if (bounds) {
      mapRef.current.fitBounds(bounds, {
        padding: { top: 64, bottom: 56, left: 32, right: 32 }, maxZoom: 17, duration: 500,
      })
      hasFitBoundsRef.current = true
    }
  }, [isMapLoaded, stops])

  const handleMapClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (import.meta.env.DEV) publishMapClick(event.lngLat)
      if (!event.features || event.features.length === 0) return
      const feature = event.features[0]
      if (feature.layer.id === LYR.STOP_CIRCLES) {
        const key = feature.properties?.key
        if (key) onSelectStop(key)
      }
    },
    [onSelectStop],
  )

  const { label: reoptimizeLabel, disabled: reoptimizeDisabled, hidden: reoptimizeHidden } = reoptimizeButtonState(
    optimizeBlockedReason, routingUnavailable, isRouteOptimized, orderLocked)
  const routeChip = ROUTE_STATE_LABEL[routeState]

  return (
    <Box position="relative" w="100%" h="100%">
      <Map
        ref={mapRef}
        mapLib={maplibregl}
        initialViewState={{ longitude: -98.5795, latitude: 39.8283, zoom: 4 }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={getMapStyle()}
        onLoad={() => setIsMapLoaded(true)}
        onClick={handleMapClick}
        interactiveLayerIds={[LYR.STOP_CIRCLES]}
      >
        {/* Full route line (semi-transparent) */}
        {routeGeoJson && (
          <Source id={SRC.ROUTE} type="geojson" data={routeGeoJson}>
            <Layer
              id={LYR.ROUTE_LINE}
              type="line"
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{ 'line-color': WALK_MAP_COLORS.route, 'line-width': 3, 'line-opacity': 0.6 }}
            />
          </Source>
        )}

        {/* Next segment line (bold) */}
        {nextSegmentGeoJson && (
          <Source id={SRC.NEXT_SEGMENT} type="geojson" data={nextSegmentGeoJson}>
            <Layer
              id={LYR.NEXT_SEGMENT_LINE}
              type="line"
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{ 'line-color': WALK_MAP_COLORS.nextSegment, 'line-width': 5, 'line-opacity': 1 }}
            />
          </Source>
        )}

        {/* Stop pins (filtered-out stops are ghosted) */}
        <Source id={SRC.STOPS} type="geojson" data={stopGeoJson} cluster={false}>
          <Layer
            id={LYR.STOP_CIRCLES}
            type="circle"
            // The selected pin paints on top, then visible pins over ghosted ones.
            layout={{ 'circle-sort-key': ['case', ['get', 'isSelected'], 2, ['get', 'isVisible'], 1, 0] }}
            paint={{
              'circle-color': ['case', ['get', 'isVisible'], ['get', 'color'], WALK_MAP_COLORS.ghost],
              'circle-radius': ['case',
                ['get', 'isSelected'], 16,
                ['get', 'isVisible'], ['interpolate', ['linear'], ['get', 'residentCount'], 1, 10, 5, 14, 10, 18],
                6],
              'circle-stroke-width': ['case', ['get', 'isSelected'], 4, ['get', 'isVisible'], 2, 1],
              'circle-stroke-color': ['case', ['get', 'isSelected'], WALK_MAP_COLORS.selectedRing, WALK_MAP_COLORS.pinStroke],
              'circle-opacity': ['case', ['get', 'isVisible'], 0.9, 0.3],
            }}
          />
          {/* List-sequence numbers on visible, open stops only */}
          <Layer
            id={LYR.STOP_LABELS}
            type="symbol"
            filter={['all', ['get', 'isOpen'], ['get', 'isVisible']]}
            layout={{
              'text-field': ['to-string', ['get', 'seq']],
              'text-font': MAP_LABEL_FONT,
              'text-size': 12,
              'text-allow-overlap': true,
            }}
            paint={{ 'text-color': WALK_MAP_COLORS.label }}
          />
        </Source>

        {/* Worksite kind badges (top-right of the pin; never intercept pin taps) */}
        {worksiteBadgeStops.map(s => (
          <Marker key={`kind-${s.key}`} longitude={s.lng!} latitude={s.lat!} anchor="bottom-left"
            offset={[6, -6]} style={{ pointerEvents: 'none' }}>
            <Box pointerEvents="none" w="18px" h="18px" borderRadius="full" bg="surface" color="fg"
              borderWidth="1px" borderColor="border" display="flex" alignItems="center" justifyContent="center">
              <Box as="span" role="img" aria-label="Worksite" display="inline-flex" fontSize="10px">
                <FiBriefcase />
              </Box>
            </Box>
          </Marker>
        ))}

        {/* GPS position marker: static ring + heading cone when heading is known */}
        {gpsPosition && (
          <Marker longitude={gpsPosition.lng} latitude={gpsPosition.lat} anchor="center">
            <Box position="relative" w="18px" h="18px">
              {Number.isFinite(gpsPosition.heading) && (
                <Box position="absolute" left="50%" bottom="50%" w={0} h={0} transformOrigin="50% 100%"
                  transform={`translateX(-50%) rotate(${gpsPosition.heading}deg)`}
                  borderLeft="7px solid transparent" borderRight="7px solid transparent"
                  style={{ borderBottom: `16px solid ${WALK_MAP_COLORS.gps}` }} opacity={0.5} aria-hidden />
              )}
              <Box position="absolute" inset={0} borderRadius="full" borderWidth="3px" borderColor="surface"
                style={{ background: WALK_MAP_COLORS.gps, boxShadow: `0 0 0 4px ${WALK_MAP_COLORS.gps}33` }} />
            </Box>
          </Marker>
        )}
      </Map>

      {/* ONE top overlay: row 1 = filter pill + controls (wraps), row 2 = in-flow status stack. */}
      <Box position="absolute" top={3} left={3} right={3} zIndex={2} pointerEvents="none"
        display="flex" flexDirection="column" gap={2}>
        <Box display="flex" flexWrap="wrap" alignItems="flex-start" gap={2}>
          {/* Filter indicator pill (shows when filter is active) */}
          {activeFilterLabel && (
            <Box
              pointerEvents="auto"
              bg="surface"
              borderRadius="md"
              shadow="md"
              px={3}
              py={1.5}
              maxW="200px"
            >
              <Text fontSize="xs" fontWeight="semibold" color="fg" lineClamp={1}>
                {activeFilterLabel}
              </Text>
            </Box>
          )}

          {/* Controls (right of the filter pill; wrap below it when they don't fit): walk/drive toggle + re-optimize */}
          <HStack gap={2} ml="auto" pointerEvents="auto">
            <HStack bg="surface" borderRadius="md" shadow="md" p={0.5} gap={0}>
              <Button
                minH="44px"
                size="sm"
                variant={travelMode === 'walking' ? 'solid' : 'ghost'}
                colorPalette={travelMode === 'walking' ? 'brand' : 'gray'}
                onClick={() => onTravelModeChange('walking')}
                borderRightRadius={0}
              >
                Walk
              </Button>
              <Button
                minH="44px"
                size="sm"
                variant={travelMode === 'driving' ? 'solid' : 'ghost'}
                colorPalette={travelMode === 'driving' ? 'brand' : 'gray'}
                onClick={() => onTravelModeChange('driving')}
                borderLeftRadius={0}
              >
                Drive
              </Button>
            </HStack>

            {!reoptimizeHidden && <Button
              aria-label={reoptimizeLabel}
              minH="44px"
              size="sm"
              bg={isRouteOptimized ? 'accent' : 'surface'}
              color={isRouteOptimized ? 'accent.fg' : 'fg.muted'}
              shadow="md"
              onClick={onReoptimize}
              loading={isRefreshing}
              disabled={reoptimizeDisabled && !isRefreshing}
              _hover={{ bg: isRouteOptimized ? 'accent.strong' : 'bg.subtle' }}
            >
              <FiZap />{reoptimizeLabel}
            </Button>}
          </HStack>

        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2}>
          {routeChip && (
            <Box pointerEvents="auto" bg="surface" borderRadius="full" shadow="md" px={3} py={1}>
              <Text fontSize="xs" fontWeight="semibold" color="fg.muted">{routeChip}</Text>
            </Box>
          )}
          {!reoptimizeHidden && !orderLocked && !replanTipSeen && (
            <HStack pointerEvents="auto" role="note" bg="surface" borderRadius="md" shadow="md" pl={3} pr={1} py={1} gap={1} alignItems="center">
              <Text fontSize="xs" color="fg">
                Your route is planned once at the start and stays put. Tap Re-plan to recalculate from where you are.
              </Text>
              <IconButton aria-label="Dismiss tip" minW="44px" minH="44px" variant="ghost" onClick={onDismissReplanTip}><FiX /></IconButton>
            </HStack>
          )}
          {topStatus}
        </Box>
      </Box>

      {/* Recentre (bottom-right, thumb reach above the dock) */}
      <IconButton
        aria-label="Center on my location"
        position="absolute"
        bottom={3}
        right={3}
        zIndex={2}
        minW="44px"
        minH="44px"
        bg="surface"
        color="fg"
        shadow="md"
        onClick={async () => {
          const pos = (await onRecenter()) ?? gpsPosition
          if (!pos) { toaster.create({ type: 'warning', title: locationUnavailableMessage(window.isSecureContext) }); return }
          mapRef.current?.flyTo({ center: [pos.lng, pos.lat], zoom: 17, duration: 500 })
        }}
      >
        <FiCrosshair />
      </IconButton>

      {/* Distance / ETA pill (bottom-centre, just above the dock) */}
      {nextDistance != null && nextDuration != null && (
        <Box
          position="absolute"
          bottom={3}
          left="50%"
          transform="translateX(-50%)"
          bg="surface"
          borderRadius="full"
          shadow="md"
          px={4}
          py={1.5}
          zIndex={2}
        >
          <Text fontSize="sm" fontWeight="semibold" color="fg">
            {formatDistance(nextDistance)} · {formatDuration(nextDuration)}
          </Text>
        </Box>
      )}

      {/* Routing-unavailable note (same bottom-centre slot; the distance pill is never
          present at the same time, because an unrouted response carries no next segment) */}
      {routingUnavailable && nextDistance == null && (
        <Box
          position="absolute"
          bottom={3}
          left="50%"
          transform="translateX(-50%)"
          bg="surface"
          borderRadius="full"
          shadow="md"
          px={4}
          py={1.5}
          zIndex={2}
        >
          <Text fontSize="sm" color="fg.muted">
            Routing unavailable — showing list order
          </Text>
        </Box>
      )}
    </Box>
  )
}
